<script setup lang="ts">
  // Open/close ceremony cash-count modal.
  // Opened via showDynamicDialog<IPosV2CajaCashResult | null>(..., 'caja-cash').
  import { computed, getCurrentInstance, ref } from 'vue';
  import { roundPrice } from '@/utils';
  import { useModal } from '@/composition/use-modal';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  export interface IPosV2CajaCashResult {
    amount: number;
  }

  const props = withDefaults(
    defineProps<{
      title: string;
      message: string;
      /** Prefill (e.g. the caja's tracked cash on open). */
      initial?: number | null;
      currencySymbol?: string;
      confirmLabel: string;
    }>(),
    {
      initial: null,
      currencySymbol: '$',
    }
  );

  const modals = useModal();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const inputValue = ref(
    props.initial != null ? String(roundPrice(props.initial)) : ''
  );
  const touched = ref(false);

  const parsedAmount = computed<number | null>(() => {
    const raw = inputValue.value.trim().replace(',', '.');
    if (!raw) return null;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return roundPrice(parsed);
  });

  const showError = computed(
    () => touched.value && inputValue.value.trim() !== '' && parsedAmount.value == null
  );

  function onInput(value: string): void {
    touched.value = true;
    inputValue.value = value;
  }

  function submit(): void {
    if (parsedAmount.value == null) {
      touched.value = true;
      return;
    }
    modals.dialogSubmit('caja-cash', { amount: parsedAmount.value });
  }
</script>

<template>
  <div class="pos-v2-caja-cash-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ title }}</h2>
      <span class="modal-subtitle">{{ message }}</span>
    </div>

    <div class="modal-body">
      <PosUiInput
        :value="inputValue"
        type="text"
        inputmode="decimal"
        size="lg"
        :prefix="currencySymbol"
        :placeholder="i18nT('cajaV2.cashPlaceholder')"
        class="cash-input"
        @input="onInput"
        @enter="submit"
      />
      <span v-if="showError" class="input-error">
        {{ $t('cajaV2.cashInvalid') }}
      </span>
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-cancel"
        @click="modals.dialogSubmit('caja-cash', null)"
      >
        {{ $t('posV2.cancel') }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        variant="solid"
        size="lg"
        class="btn-confirm"
        :disabled="parsedAmount == null"
        @click="submit"
      >
        {{ confirmLabel }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-caja-cash-modal {
    @apply tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4;

    .modal-header {
      @apply tw-flex tw-flex-col tw-gap-0.5;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }

      .modal-subtitle {
        @apply tw-text-sm tw-text-neutral-500;
      }
    }

    .modal-body {
      @apply tw-flex tw-flex-col tw-gap-1.5;

      .cash-input {
        @apply tw-w-full;
      }

      .input-error {
        @apply tw-text-sm tw-text-red-600;
      }
    }

    .modal-footer {
      @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2;

      :deep(.btn-cancel),
      :deep(.btn-confirm) {
        @apply tw-min-w-[130px];
      }
    }
  }
</style>
